import Config from '@/Management/GlobalConfiguration';
import { IListViewDTO } from "coreframetypings/dto";
import ComponentManager from '@/Management/ComponentManager';
import LocatorHelper from '@/Utility/LocatorHelper';
import RADApplication from '@/Management/RADApplication';
import StorageHelper from '@/Utility/StorageHelper';
import ListViewHelper from '@/Utility/ListViewHelper';
import UserSpecificDataHelper from '@/Utility/UserSpecificDataHelper';
import { IDataGridViewModel, IDataGridViewModelConstructorParameter, IFilterViewModel, IFilterViewModelConstructorParameter, IListViewViewModel, SortElement } from '#/DefaultViewExtensionInterfaces';
import Utilities from '@/Utility/Utilities';
import NavigationService from '@/Management/NavigationService';

export default class ListViewDataGridHelper<TDataGrid extends IDataGridViewModel = IDataGridViewModel,
    TDataGridCSP extends IDataGridViewModelConstructorParameter = IDataGridViewModelConstructorParameter> {

    private additionalFilterParameters: IFilterViewModelConstructorParameter;
    private additionalDataGridParameters: TDataGridCSP;
    private filterViewModel: IFilterViewModel;
    private dataGrid: TDataGrid;
    private listViewPrefix: string;
    private storageName: string;
    private dataGridImplementation: new (...args: any) => TDataGrid;
    private orderBy: KnockoutComputed<SortElement[]>;

    constructor(private listView: IListViewViewModel, private listViewDto: IListViewDTO) {
        this.storageName = listViewDto.name;
        this.dataGridImplementation = ComponentManager.getViewModel<TDataGrid>('DataGridViewModel');
    }

    /**
     * creates parameters for datagrid
     */
    public createDataGridViewModelParameters(): TDataGridCSP {
        this.createFilterViewModel();

        let columnPreset = StorageHelper.bind(this.storageName + '_ColumnPreset', {});
        let pageSize = UserSpecificDataHelper.loadUserSpecificData(this.storageName + '_PageSize', RADApplication.getInstance().configuration.possibleListViewPages[0]);
        const orderBy = this.getOrderBy();

        let storageColumnDefinitionKey = this.listView.getStorageColumnDefinitionKey(this.listViewDto);
        let columnDefinition = ListViewHelper.loadColumnDefinitions(storageColumnDefinitionKey, this.listViewDto.columns);

        this.listView.addComputed(columnPreset);
        this.listView.addComputed(pageSize);

        const staticFilter = this.listView.filter || this.listViewDto.filter;

        return $.extend({
            locator: this.createLocator('datagrid'),
            parentLocator: this.listView.locator,
            identity: 'PDO_ID',
            columnDefinition: columnDefinition,
            storageColumnDefinitionKey: storageColumnDefinitionKey,
            filter: this.filterViewModel,
            pageSizes: RADApplication.getInstance().configuration.possibleListViewPages,
            pageSize: pageSize,
            columnPreset: columnPreset,
            orderBy: orderBy,
            isMultiColumnSort: true,
            initial: this.listView.selectedPDO,
            preFilter: this.listView.preFilter,
            staticFilter: staticFilter
        }, this.additionalDataGridParameters);
    }

    /**
     * returns the computed orderby-statement which is used in datagrid AND filter (clone)
     */
    private getOrderBy(): KnockoutComputed<SortElement[]> {
        if (this.orderBy != null)
            return this.orderBy;

        const defaultValue = [];
        const limit = this.listViewDto.rowLimit;
        const useUSD = RADApplication.getInstance().isSystemParamSetAndTrue('Gui.SaveListViewSorting') && (isNaN(limit) || limit <= 0)

        if (useUSD) {
            this.orderBy = UserSpecificDataHelper.loadUserSpecificData(this.listViewDto.name + '_Order', defaultValue)
        } else {
            const observable = ko.observable(defaultValue);
            return ko.computed({
                read: () => {
                    return observable();
                },
                write: (newVal) => {
                    observable(newVal);
                }
            });
        }

        this.listView.addComputed(this.orderBy);

        return this.orderBy;
    }

    /**
     * creates viewmodel for datagrid
     * @param customParams use custom params instead
     */
    public createDataGridViewModel(customParams?: TDataGridCSP): TDataGrid {
        if (this.dataGrid != null)
            return this.dataGrid;

        this.dataGrid = new this.dataGridImplementation(customParams || this.createDataGridViewModelParameters());
        this.createFilterSubscriptions();

        return this.dataGrid;
    }

    /**
     * sets prefix for the listview (used in locator etc.)
     * @param name
     */
    public setListViewPrefix(name: string): void {
        this.listViewPrefix = name;
    }

    /**
     * sets storage name of the listview (used for storage like column presets etc.)
     * @param name
     */
    public setStorageName(name: string): void {
        this.storageName = name;
    }

    /**
     * sets the datagrid implementation
     * @param implementation
     */
    public setDataGridImplementation<T extends TDataGrid>(implementation: new (...args: any[]) => T): void {
        this.dataGridImplementation = implementation;
    }

    private createFilterSubscriptions() {
        // refresh the grid when the sort-state of any sortElement-VM changes
        this.listView.defineSubscription(this.filterViewModel.orderBy,
            filterVM_orderBy => {
                if (this.filterViewModel.suspendOrderByUpdateToGrid !== true) {
                    this.dataGrid.onSortChanged(filterVM_orderBy);
                }
            });

        // forward changes from grid-ordering to filter-VM-ordering
        this.listView.defineSubscription(this.dataGrid.orderBy, orderBy => {
            this.filterViewModel.suspendOrderByUpdateToGrid = true;
            this.filterViewModel.orderBy(orderBy);
            this.filterViewModel.suspendOrderByUpdateToGrid = false;
        });

        this.listView.defineSubscription(this.dataGrid.filter.serializedFilter, (sf) => {
            NavigationService.getInstance().setHiddenParameterToAddress(this.listView.identifier, 'SerializedFilter', sf);
        });
    }

    public setAdditionalFilterConstructorParameter(params: IFilterViewModelConstructorParameter): void {
        this.additionalFilterParameters = params;
    }

    public setAdditionalDataGridConstructorParameters(params: TDataGridCSP): void {
        this.additionalDataGridParameters = params;
    }

    public createFilterViewModel(): void {
        if (this.filterViewModel != null)
            return;

        const staticFilter = this.listView.filter || this.listViewDto.filter;
        
        let params = $.extend({
            locator: this.createLocator('datagrid', 'filter'),
            parentLocator: this.listView.locator,
            serializedFilter: this.listView.serializedFilter,
            onSubmit: () => {
                this.listView.refresh();
            },
            includeAllPrincipals: this.listViewDto.includeAllPrincipals,
            includeBasePrincipals: this.listViewDto.includeBasePrincipals,
            includeSubPrincipals: this.listViewDto.includeSubPrincipals,
            listViewPdoId: this.listViewDto.listViewID,
            pdTypeName: this.listViewDto.typeName,
            preFilter: this.listView.preFilter,
            staticFilter: staticFilter,
            orderBy: this.getOrderBy()() // clone !!! there are subscriptions for sync between both orderby-observables
        }, this.additionalFilterParameters);

        const filterViewModel = ComponentManager.getViewModelByInterface<IFilterViewModel>('FilterViewModel');

        this.filterViewModel = new filterViewModel(params);
    }

    public createLocator(...additionalLocator: string[]): ReturnType<typeof LocatorHelper.buildLocator> {
        if (Utilities.isNullOrUndefinedOrBlank(this.listViewPrefix))
            additionalLocator.splice(0, 0, this.listViewDto.typeName)
        else
            additionalLocator.splice(0, 0, this.listViewPrefix, this.listViewDto.typeName);

        return LocatorHelper.buildLocator.apply(null, additionalLocator)
    }
}